import { useMemo, useState } from 'react'
import SegmentedControl from '../Shared/SegmentedControl'
import UserAvatar from '../Shared/UserAvatar'
import { TICKET_TYPE_TREE } from '../../utils/ticketType'
import {
  EMPTY_QUEUE,
  QUEUE_COLORS,
  QUEUE_STRATEGIES,
  typeOwners,
  validateQueue,
} from '../../utils/queue'
import { employeeName } from '../../utils/employee'

const STRATEGY_OPTIONS = Object.entries(QUEUE_STRATEGIES).map(
  ([value, meta]) => ({
    value,
    label: meta.label,
    icon: `fa-solid ${meta.icon}`,
  })
)

/** Normaliza texto para buscar sin tildes ni mayúsculas. */
const normalize = (text) =>
  String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')

/**
 * Formulario de una cola de atención: datos, tipos de registro que atiende,
 * estrategia de asignación y miembros.
 *
 * @component
 * @param {Object} props
 * @param {Object|null} props.queue - Cola a editar (null = nueva).
 * @param {Array<Object>} props.queues - Todas las colas (para validar conflictos).
 * @param {Array<Object>} props.employees - Empleados elegibles como miembros.
 * @param {boolean} props.saving
 * @param {(draft: Object) => Promise<boolean>} props.onSubmit
 * @param {() => void} props.onCancel
 * @returns {JSX.Element}
 */
const QueueForm = ({
  queue,
  queues,
  employees,
  saving,
  onSubmit,
  onCancel,
}) => {
  const [draft, setDraft] = useState(() =>
    queue
      ? {
          name: queue.name,
          description: queue.description || '',
          color: queue.color,
          strategy: queue.strategy,
          active: queue.active,
          types: queue.types,
          memberIds: queue.memberIds,
        }
      : EMPTY_QUEUE
  )
  const [errors, setErrors] = useState({})
  const [memberSearch, setMemberSearch] = useState('')

  const owners = useMemo(
    () => typeOwners(queues, queue?.id ?? null),
    [queues, queue]
  )

  const set = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: undefined }))
  }
  const toggleIn = (key, value) =>
    set(
      key,
      draft[key].includes(value)
        ? draft[key].filter((v) => v !== value)
        : [...draft[key], value]
    )

  // Seleccionados primero; luego el resto filtrado por la búsqueda.
  const visibleEmployees = useMemo(() => {
    const term = normalize(memberSearch.trim())
    return employees
      .filter(
        (e) =>
          draft.memberIds.includes(e.id) ||
          !term ||
          normalize(employeeName(e)).includes(term)
      )
      .sort(
        (a, b) =>
          Number(draft.memberIds.includes(b.id)) -
            Number(draft.memberIds.includes(a.id)) ||
          employeeName(a).localeCompare(employeeName(b))
      )
  }, [employees, memberSearch, draft.memberIds])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const found = validateQueue(draft, queues, queue?.id ?? null)
    setErrors(found)
    if (Object.values(found).some(Boolean)) return
    await onSubmit(draft)
  }

  return (
    <form className="qf" onSubmit={handleSubmit} noValidate>
      {/* DATOS */}
      <div className="row g-3">
        <div className="col-md-8">
          <label className="qf-label" htmlFor="qf-name">
            Nombre <span className="text-danger">*</span>
          </label>
          <input
            id="qf-name"
            className={`form-control form-control-sm ${errors.name ? 'is-invalid' : ''}`}
            value={draft.name}
            maxLength={80}
            placeholder="Ej: Soporte nivel 1"
            onChange={(e) => set('name', e.target.value)}
            autoFocus
          />
          {errors.name && <p className="qf-error">{errors.name}</p>}
        </div>
        <div className="col-md-4">
          <label className="qf-label">Color</label>
          <div className="qf-colors">
            {QUEUE_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={`qf-color ${draft.color === color ? 'is-active' : ''}`}
                style={{ background: color }}
                onClick={() => set('color', color)}
                aria-label={`Color ${color}`}
                aria-pressed={draft.color === color}
              />
            ))}
          </div>
        </div>
        <div className="col-12">
          <label className="qf-label" htmlFor="qf-desc">
            Descripción
          </label>
          <input
            id="qf-desc"
            className="form-control form-control-sm"
            value={draft.description}
            maxLength={200}
            placeholder="Qué atiende esta cola (opcional)"
            onChange={(e) => set('description', e.target.value)}
          />
        </div>
      </div>

      {/* TIPOS */}
      <div className="qf-section">
        <label className="qf-label">
          Tipos de registro que atiende <span className="text-danger">*</span>
        </label>
        {TICKET_TYPE_TREE.map((group) => (
          <div key={group.value} className="qf-type-group">
            <span className="qf-type-family">{group.label}</span>
            <div className="qf-chips">
              {group.types.map((type) => {
                const selected = draft.types.includes(type.value)
                const owner = owners[type.value]
                return (
                  <button
                    key={type.value}
                    type="button"
                    className={`qf-chip ${selected ? 'is-active' : ''}`}
                    style={
                      selected
                        ? {
                            color: type.color,
                            borderColor: type.color,
                            background: `${type.color}14`,
                          }
                        : undefined
                    }
                    disabled={!!owner}
                    title={
                      owner
                        ? `Ya lo atiende la cola «${owner.name}»`
                        : type.hint
                    }
                    onClick={() => toggleIn('types', type.value)}
                    aria-pressed={selected}
                  >
                    <i className={`fa-solid ${type.icon}`} />
                    {type.label}
                    {owner && <span className="qf-owner">· {owner.name}</span>}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
        {errors.types && <p className="qf-error">{errors.types}</p>}
      </div>

      {/* ESTRATEGIA */}
      <div className="qf-section">
        <label className="qf-label">Asignación automática</label>
        <div className="d-flex flex-wrap align-items-center gap-3">
          <SegmentedControl
            ariaLabel="Estrategia"
            options={STRATEGY_OPTIONS}
            value={draft.strategy}
            onChange={(value) => set('strategy', value)}
          />
          <span className="qf-hint">
            {QUEUE_STRATEGIES[draft.strategy]?.hint}
          </span>
        </div>
      </div>

      {/* MIEMBROS */}
      <div className="qf-section">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <label className="qf-label mb-0">
            Miembros <span className="qf-hint">({draft.memberIds.length})</span>
          </label>
          <input
            type="search"
            className="form-control form-control-sm qf-member-search"
            placeholder="Buscar empleado…"
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
          />
        </div>
        <div className="qf-members">
          {visibleEmployees.length === 0 ? (
            <p className="qf-hint p-2 mb-0">Sin resultados.</p>
          ) : (
            visibleEmployees.map((emp) => (
              <label key={emp.id} className="qf-member">
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={draft.memberIds.includes(emp.id)}
                  onChange={() => toggleIn('memberIds', emp.id)}
                />
                <UserAvatar employee={emp} size={22} />
                <span>{employeeName(emp) || emp.id}</span>
              </label>
            ))
          )}
        </div>
        {errors.memberIds && <p className="qf-error">{errors.memberIds}</p>}
      </div>

      {/* ACTIVA + ACCIONES */}
      <div className="qf-footer">
        <label className="form-check form-switch m-0">
          <input
            type="checkbox"
            className="form-check-input"
            checked={draft.active}
            onChange={(e) => set('active', e.target.checked)}
          />
          <span className="form-check-label small">
            Cola activa (asigna tickets nuevos)
          </span>
        </label>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={onCancel}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-sm btn-primary"
            disabled={saving}
          >
            {saving && (
              <span className="spinner-border spinner-border-sm me-2" />
            )}
            Guardar cola
          </button>
        </div>
      </div>

      <style>{`
        .qf { display: flex; flex-direction: column; gap: 16px; }
        .qf-label { display: block; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--gm-text-muted); margin-bottom: 6px; }
        .qf-hint { font-size: 0.78rem; color: var(--gm-text-muted); font-weight: 400; text-transform: none; letter-spacing: 0; }
        .qf-error { margin: 6px 0 0; font-size: 0.75rem; color: var(--gm-danger); }
        .qf-colors { display: flex; flex-wrap: wrap; gap: 6px; }
        .qf-color { width: 22px; height: 22px; border-radius: 50%; border: 2px solid transparent; padding: 0; }
        .qf-color.is-active { box-shadow: 0 0 0 2px var(--gm-surface), 0 0 0 4px var(--gm-text); }
        .qf-section { padding-top: 14px; border-top: 1px solid var(--gm-border); }
        .qf-type-group { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 8px; }
        .qf-type-family { width: 86px; flex-shrink: 0; padding-top: 5px; font-size: 0.75rem; color: var(--gm-text-muted); }
        .qf-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .qf-chip {
          display: inline-flex; align-items: center; gap: 6px; height: 28px; padding: 0 10px;
          border-radius: 99px; border: 1px solid var(--gm-border); background: transparent;
          color: var(--gm-text-muted); font-size: 0.78rem; font-weight: 500;
        }
        .qf-chip:hover:not(:disabled) { color: var(--gm-text); border-color: var(--gm-text-faint); }
        .qf-chip:disabled { opacity: 0.5; cursor: not-allowed; }
        .qf-chip i { font-size: 0.7rem; }
        .qf-owner { font-size: 0.7rem; }
        .qf-member-search { max-width: 220px; }
        .qf-members {
          max-height: 220px; overflow-y: auto; border: 1px solid var(--gm-border);
          border-radius: 8px; padding: 4px;
        }
        .qf-member {
          display: flex; align-items: center; gap: 8px; padding: 5px 8px; border-radius: 6px;
          font-size: 0.82rem; color: var(--gm-text); cursor: pointer;
        }
        .qf-member:hover { background: var(--gm-surface-2); }
        .qf-footer {
          display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
          gap: 10px; padding-top: 14px; border-top: 1px solid var(--gm-border);
        }
      `}</style>
    </form>
  )
}

export default QueueForm
